import _ from 'lodash';

import {
  Box,
  Button,
  Skeleton,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import {
  useCallback,
  useMemo,
  useState
} from 'react';

import {
  CompanyContextCategoryName,
  StockAnalysisSectionCode
} from 'generated/graphql';
import {
  CustomIcons,
  EarningsCallSummaryButton,
  RevenueDeepDiveButton,
  StockAnalysisButton,
  useLoadingOverlay
} from 'modules/components/layer00';
import { MediumIconType } from 'modules/components/layer00/CustomIcon';
import { SpecifyGlAccountModal } from 'modules/components/layer33';
import * as ds from './dataService';



const __categoryName2Icon: Record<CompanyContextCategoryName, MediumIconType> = {
  valuation: 'PIECHART',
  competitiveStrategy: 'SETTINGS',
  revenue: 'LINECHART',
  liquidity: 'CREDITCARD',
  dividends: 'ATTACHMENT',
  margins: 'PIECHART',
  costStructureAndEfficiency: 'STAR',
  guidance: 'PIN',
  earnings: 'PRICING',
  quarterlyReports: 'QUARTERLYREPORTS'
};



export type PickCompanyCategoryMessageProps = {
  selectedCategoryName: CompanyContextCategoryName | null;
  listedCompanyId: string;

  onSelectCategory: (category: CompanyContextCategoryName | null) => void;
  onSelectContextQuestion: (contextQuestionId: string) => void;
  onSelectGlAccount: (glAccountId: string) => void;
};

export const FC = (props: PickCompanyCategoryMessageProps) => {
  // This should not be here. But placing here to save time for now. We do this to hide the questions which only make sense in a company-specific context.
  const [
    modalVisible,
    setModalVisible
  ] = useState(false);
  const queryTuple = ds.useQuery(props.listedCompanyId);
  useLoadingOverlay(queryTuple);

  const handleClickCategory = useCallback(
    (category: ds.IData['categories'][number]) => {
      if (props.selectedCategoryName === category.name) {
        props.onSelectCategory(null);
      } else {
        props.onSelectCategory(category.name);
      }
    },
    [props]
  );

  const categoryQuestions = useMemo(
    () => queryTuple.data.questions
      .filter((q) => q.name === props.selectedCategoryName)
      // Filter out other buttons from revenue for now until we hook up pipeline.
      .filter((q) => {
        if (q.name === 'revenue') {
          return [
            'Revenue Deep Dive',
            // 'Revenue by Geography (%)',
            // 'Revenue by Product (%)',
            'Revenue by Geography ($)',
            'Revenue by Product ($)'
          ].includes(q.content);
        }

        return true;
      }),
    [
      queryTuple.data.questions,
      props.selectedCategoryName
    ]
  );

  const sectionCodes: StockAnalysisSectionCode[] = [
    'businessModel',
    'competitiveAdvantages',
    'growthOpportunities',
    'keyVariables',
    'fundamentals',
    'theBet'
  ];

  return (
    <>
      {modalVisible && (
        <SpecifyGlAccountModal.FC
          isVisible={modalVisible}
          handleClose={() => setModalVisible(false)}
        />
      )}

      <Box>
        <Stack
          direction='row'
          justifyContent='space-between'
          paddingBottom='.5rem'
        >
          <Typography
            variant='chatMessageTitle'
            fontWeight='600'
            color='#fff'
          >
            PICK A COMPANY PROFILE CATEGORY
          </Typography>

          <Typography
            variant='chatMessageTitle'
          >
            {queryTuple.data.categories.length.toLocaleString('en-US', {
              minimumIntegerDigits: 2
            })}
          </Typography>
        </Stack>

        <Box
          display='inline-flex'
          flexWrap='wrap'
          gap='.5rem'
          sx={{
            mb: 4
          }}
        >
          <StockAnalysisButton.FC
            variant='chat'
            companyId={props.listedCompanyId}
          />

          <RevenueDeepDiveButton.FC
            variant='chat'
            companyId={props.listedCompanyId}
            onClick={() => {
              const revenueDeepDive = queryTuple.data.questions.find((q) => q.content === 'Revenue Deep Dive');
              if (revenueDeepDive) {
                props.onSelectContextQuestion(revenueDeepDive.id);
              }
            }}
          />

          <EarningsCallSummaryButton.FC
            variant='chat'
            companyId={props.listedCompanyId}
            onClick={() => {
              const earningsCall = queryTuple.data.questions.find((q) => q.content === 'Earnings Call Summary');
              if (earningsCall) {
                props.onSelectContextQuestion(earningsCall.id);
              }
            }}
          />

          <EarningsCallSummaryButton.FC
            variant='chat'
            companyId={props.listedCompanyId}
            isRiskAssessment
            onClick={() => {
              const earningsCall = queryTuple.data.questions.find((q) => q.content === 'Sherlocks Risk Assessment');
              if (earningsCall) {
                props.onSelectContextQuestion(earningsCall.id);
              }
            }}
          />

          {sectionCodes.map((code) => (
            <StockAnalysisButton.FC
              key={code}
              variant='chat'
              companyId={props.listedCompanyId}
              sectionCode={code}
            />
          ))}

          {queryTuple.loading && _.times(10, (i) => (
            <Button
              variant='chatCategorySelect'
              key={i}
              disabled
            >
              <Skeleton sx={{
                minWidth: 100
              }}
              />
            </Button>
          ))}

          {!queryTuple.loading && queryTuple.data.categories.map((category) => {
            const hasQuestions = queryTuple.data.questions.some((q) => q.name === category.name);
            if (!hasQuestions) {
              return (
                <Tooltip
                  key={category.name}
                  title='coming soon'
                >
                  <Button
                    disabled
                    startIcon={<CustomIcons.MediumIcon.FC icon={__categoryName2Icon[category.name]} />}
                    variant='chatCategorySelect'
                  >
                    {category.displayName}
                  </Button>
                </Tooltip>
              );
            } else {
              const button = (
                <Button
                  sx={{
                    backgroundColor: 'white'
                  }}
                  key={category.name}
                  startIcon={<CustomIcons.MediumIcon.FC icon={__categoryName2Icon[category.name]} />}
                  variant='chatCategorySelect'
                  onClick={() => handleClickCategory(category)}
                >
                  {category.displayName}
                </Button>
              );

              return button;
            }
          })}

          <Button
            sx={{
              backgroundColor: (theme) => theme.palette.primary.darker200,
              color: (theme) => theme.palette.gray.light05
            }}
            variant='chatCategorySelect'
            onClick={() => setModalVisible(true)}
          >
            All Company Data
          </Button>
        </Box>

        {props.listedCompanyId && categoryQuestions.length > 0 && (
          <Box sx={{
            mt: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1
          }}
          >
            <Box sx={{
              display: 'flex', justifyContent: 'space-between'
            }}
            >
              <Typography
                variant='chatMessageTitle'
                fontWeight='600'
                color='#fff'
              >
                ASK A QUESTION: {queryTuple.data.categories.find((c) => c.name === props.selectedCategoryName)?.displayName.toUpperCase()}
              </Typography>

              <Typography
                variant='chatMessageTitle'
                color='#fff'
              >
                {categoryQuestions.length.toLocaleString('en-US', {
                  minimumIntegerDigits: 2
                })}
              </Typography>
            </Box>

            {categoryQuestions.map((q) => (
              <Button
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between'
                }}
                color='secondary'
                variant='contained'
                key={q.id}
                endIcon={<CustomIcons.MediumIcon.FC icon='ARROW' />}
                onClick={() => {
                  props.onSelectCategory(null);
                  if (q.type === 'contextQuestion') {
                    props.onSelectContextQuestion(q.id);
                  } else if (q.type === 'glAccount') {
                    props.onSelectGlAccount(q.id);
                  }
                }}
              >
                {q.content}
              </Button>
            ))}
          </Box>
        )}
      </Box>
    </>
  );
};
